import React, { useState, useCallback, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  Switch,
  FormControlLabel,
  MenuItem,
  Divider,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
  Alert
} from '@mui/material';
import {
  DragIndicator,
  Delete,
  Edit,
  TextFields,
  ArrowDropDown,
  CalendarToday,
  CheckBox,
  RadioButtonChecked,
  Notes,
  Phone,
  Email,
  Add,
  Save,
  FolderOpen,
  CloudUpload
} from '@mui/icons-material';

// Available field types that can be dragged from the palette
const FIELD_TYPES = [
  {
    type: 'text',
    label: 'Text Input',
    icon: TextFields,
    defaultProps: {
      label: 'Text Field',
      placeholder: 'Enter text...',
      required: false
    }
  },
  {
    type: 'email',
    label: 'Email Input',
    icon: Email,
    defaultProps: {
      label: 'Email',
      placeholder: 'Enter email...',
      required: false
    }
  },
  {
    type: 'tel',
    label: 'Phone Input',
    icon: Phone,
    defaultProps: {
      label: 'Phone',
      placeholder: '(555) 123-4567',
      required: false
    }
  },
  {
    type: 'select',
    label: 'Dropdown',
    icon: ArrowDropDown,
    defaultProps: {
      label: 'Select Option',
      required: false,
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' }
      ]
    }
  },
  {
    type: 'date',
    label: 'Date Picker',
    icon: CalendarToday,
    defaultProps: {
      label: 'Date',
      required: false
    }
  },
  {
    type: 'textarea',
    label: 'Text Area',
    icon: Notes,
    defaultProps: {
      label: 'Text Area',
      placeholder: 'Enter text...',
      rows: 4,
      required: false
    }
  },
  {
    type: 'checkbox',
    label: 'Checkbox',
    icon: CheckBox,
    defaultProps: {
      label: 'Checkbox Option',
      required: false
    }
  },
  {
    type: 'radio',
    label: 'Radio Group',
    icon: RadioButtonChecked,
    defaultProps: {
      label: 'Radio Group',
      required: false,
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' }
      ]
    }
  }
];

// Generate unique ID for form fields
const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const FormBuilder = () => {
  const [formFields, setFormFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [formTitle, setFormTitle] = useState('New Form');
  const [savedForms, setSavedForms] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load saved forms from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('formBuilderSavedForms');
    if (saved) {
      try {
        setSavedForms(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved forms:', error);
      }
    }
  }, []);

  // Save form to localStorage
  const handleSaveForm = () => {
    if (!formTitle.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a form title before saving',
        severity: 'error'
      });
      return;
    }

    if (formFields.length === 0) {
      setSnackbar({
        open: true,
        message: 'Cannot save an empty form',
        severity: 'error'
      });
      return;
    }

    const formConfig = {
      id: Date.now().toString(),
      title: formTitle,
      createdAt: new Date().toISOString(),
      fields: formFields.map(field => ({
        id: field.id,
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required,
        placeholder: field.placeholder,
        options: field.options,
        rows: field.rows
      }))
    };

    const updatedSavedForms = [...savedForms, formConfig];
    setSavedForms(updatedSavedForms);
    localStorage.setItem('formBuilderSavedForms', JSON.stringify(updatedSavedForms));

    setSnackbar({
      open: true,
      message: 'Form saved successfully!',
      severity: 'success'
    });
  };

  // Load form from saved forms
  const handleLoadForm = (formConfig) => {
    setFormTitle(formConfig.title);
    setFormFields(formConfig.fields);
    setSelectedField(null);
    setLoadDialogOpen(false);

    setSnackbar({
      open: true,
      message: 'Form loaded successfully!',
      severity: 'success'
    });
  };

  // Delete saved form
  const handleDeleteSavedForm = (formId) => {
    const updatedSavedForms = savedForms.filter(form => form.id !== formId);
    setSavedForms(updatedSavedForms);
    localStorage.setItem('formBuilderSavedForms', JSON.stringify(updatedSavedForms));

    setSnackbar({
      open: true,
      message: 'Form deleted successfully!',
      severity: 'success'
    });
  };

  // Handle drag end event
  const handleDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;

    // If dropped outside a droppable area
    if (!destination) {
      console.log('Drag cancelled - dropped outside droppable area');
      return;
    }

    // If dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      console.log('Drag cancelled - dropped in same position');
      return;
    }

    try {
      // If dragging from palette to canvas
      if (source.droppableId === 'palette' && destination.droppableId === 'canvas') {
        const fieldType = FIELD_TYPES.find(type => type.type === draggableId);
        if (fieldType) {
          const newField = {
            id: generateId(),
            name: `${fieldType.type}_${Date.now()}`,
            type: fieldType.type,
            ...fieldType.defaultProps
          };

          const newFields = Array.from(formFields);
          newFields.splice(destination.index, 0, newField);
          setFormFields(newFields);

          console.log(`Added ${fieldType.label} field to canvas`);
          setSnackbar({
            open: true,
            message: `${fieldType.label} field added to form`,
            severity: 'success'
          });
        }
        return;
      }

      // If reordering within canvas
      if (source.droppableId === 'canvas' && destination.droppableId === 'canvas') {
        const newFields = Array.from(formFields);
        const [reorderedField] = newFields.splice(source.index, 1);
        newFields.splice(destination.index, 0, reorderedField);
        setFormFields(newFields);

        console.log('Field reordered within canvas');
        return;
      }
    } catch (error) {
      console.error('Error during drag and drop:', error);
      setSnackbar({
        open: true,
        message: 'Error occurred during drag and drop operation',
        severity: 'error'
      });
    }
  }, [formFields]);

  // Handle field selection for editing
  const handleFieldSelect = (field) => {
    setSelectedField(field);
  };

  // Handle field property updates
  const handleFieldUpdate = (fieldId, updates) => {
    setFormFields(fields => 
      fields.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );

    // Update selected field if it's the one being edited
    if (selectedField && selectedField.id === fieldId) {
      setSelectedField({ ...selectedField, ...updates });
    }
  };

  // Handle field deletion
  const handleFieldDelete = (fieldId) => {
    setFormFields(fields => fields.filter(field => field.id !== fieldId));
    if (selectedField && selectedField.id === fieldId) {
      setSelectedField(null);
    }
  };

  // Add option to select/radio field
  const handleAddOption = (fieldId) => {
    const field = formFields.find(f => f.id === fieldId);
    if (field && (field.type === 'select' || field.type === 'radio')) {
      const newOption = {
        value: `option_${Date.now()}`,
        label: `Option ${(field.options?.length || 0) + 1}`
      };
      handleFieldUpdate(fieldId, {
        options: [...(field.options || []), newOption]
      });
    }
  };

  // Remove option from select/radio field
  const handleRemoveOption = (fieldId, optionIndex) => {
    const field = formFields.find(f => f.id === fieldId);
    if (field && field.options) {
      const newOptions = field.options.filter((_, index) => index !== optionIndex);
      handleFieldUpdate(fieldId, { options: newOptions });
    }
  };

  // Export form configuration
  const handleExportForm = () => {
    const formConfig = {
      title: formTitle,
      fields: formFields.map(field => ({
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required,
        placeholder: field.placeholder,
        options: field.options,
        rows: field.rows
      }))
    };

    console.log('Form Configuration:', formConfig);

    // Create downloadable JSON file
    const dataStr = JSON.stringify(formConfig, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    const exportFileDefaultName = `${formTitle.toLowerCase().replace(/\s+/g, '_')}_config.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <TextField
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveForm}
                disabled={formFields.length === 0}
                color="success"
              >
                Save Form
              </Button>
              <Button
                variant="outlined"
                startIcon={<FolderOpen />}
                onClick={() => setLoadDialogOpen(true)}
                disabled={savedForms.length === 0}
              >
                Load Form
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={handleExportForm}
                disabled={formFields.length === 0}
              >
                Export Form
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFormFields([]);
                  setSelectedField(null);
                  setFormTitle('New Form');
                }}
              >
                Clear All
              </Button>
            </Stack>
          </Box>
        </Paper>

        <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {/* Field Palette */}
          <Grid item xs={3}>
            <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Field Types
              </Typography>
              <Droppable droppableId="palette" isDropDisabled>
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {FIELD_TYPES.map((fieldType, index) => (
                      <Draggable
                        key={fieldType.type}
                        draggableId={fieldType.type}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              mb: 1,
                              cursor: 'grab',
                              opacity: snapshot.isDragging ? 0.5 : 1,
                              transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                              '&:hover': {
                                boxShadow: 2
                              }
                            }}
                          >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <fieldType.icon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {fieldType.label}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          </Grid>

          {/* Form Canvas */}
          <Grid item xs={6}>
            <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Form Canvas
              </Typography>
              <Droppable droppableId="canvas">
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      minHeight: 400,
                      border: '2px dashed',
                      borderColor: snapshot.isDraggingOver ? 'primary.main' : 'grey.300',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: snapshot.isDraggingOver ? 'primary.50' : 'transparent'
                    }}
                  >
                    {formFields.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 200,
                          color: 'text.secondary'
                        }}
                      >
                        <Typography variant="body1">
                          Drag field types here to build your form
                        </Typography>
                      </Box>
                    ) : (
                      formFields.map((field, index) => (
                        <Draggable key={field.id} draggableId={field.id} index={index}>
                          {(provided, snapshot) => (
                            <Paper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              elevation={snapshot.isDragging ? 4 : 1}
                              sx={{
                                mb: 2,
                                p: 2,
                                border: selectedField?.id === field.id ? '2px solid' : '1px solid',
                                borderColor: selectedField?.id === field.id ? 'primary.main' : 'grey.300',
                                cursor: 'pointer',
                                '&:hover': {
                                  borderColor: 'primary.main'
                                }
                              }}
                              onClick={() => handleFieldSelect(field)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                                  <DragIndicator sx={{ color: 'grey.500' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                  {field.label}
                                </Typography>
                                <Chip
                                  label={field.type}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ mr: 1 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFieldDelete(field.id);
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>

                              {/* Field Preview */}
                              <Box sx={{ opacity: 0.7, pointerEvents: 'none' }}>
                                {field.type === 'select' ? (
                                  <TextField
                                    select
                                    fullWidth
                                    label={field.label}
                                    size="small"
                                    value=""
                                  >
                                    {field.options?.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                ) : field.type === 'textarea' ? (
                                  <TextField
                                    fullWidth
                                    label={field.label}
                                    multiline
                                    rows={field.rows || 4}
                                    size="small"
                                    placeholder={field.placeholder}
                                  />
                                ) : field.type === 'checkbox' ? (
                                  <FormControlLabel
                                    control={<CheckBox />}
                                    label={field.label}
                                  />
                                ) : (
                                  <TextField
                                    fullWidth
                                    label={field.label}
                                    type={field.type}
                                    size="small"
                                    placeholder={field.placeholder}
                                  />
                                )}
                              </Box>
                            </Paper>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          </Grid>

          {/* Properties Panel */}
          <Grid item xs={3}>
            <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Field Properties
              </Typography>

              {selectedField ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Field Label"
                    value={selectedField.label || ''}
                    onChange={(e) => handleFieldUpdate(selectedField.id, { label: e.target.value })}
                    sx={{ mb: 2 }}
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Field Name"
                    value={selectedField.name || ''}
                    onChange={(e) => handleFieldUpdate(selectedField.id, { name: e.target.value })}
                    sx={{ mb: 2 }}
                    size="small"
                  />

                  {(selectedField.type === 'text' || selectedField.type === 'email' || selectedField.type === 'tel' || selectedField.type === 'textarea') && (
                    <TextField
                      fullWidth
                      label="Placeholder"
                      value={selectedField.placeholder || ''}
                      onChange={(e) => handleFieldUpdate(selectedField.id, { placeholder: e.target.value })}
                      sx={{ mb: 2 }}
                      size="small"
                    />
                  )}

                  {selectedField.type === 'textarea' && (
                    <TextField
                      fullWidth
                      label="Rows"
                      type="number"
                      value={selectedField.rows || 4}
                      onChange={(e) => handleFieldUpdate(selectedField.id, { rows: parseInt(e.target.value) })}
                      sx={{ mb: 2 }}
                      size="small"
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedField.required || false}
                        onChange={(e) => handleFieldUpdate(selectedField.id, { required: e.target.checked })}
                      />
                    }
                    label="Required"
                    sx={{ mb: 2 }}
                  />

                  {(selectedField.type === 'select' || selectedField.type === 'radio') && (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2">Options</Typography>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => handleAddOption(selectedField.id)}
                        >
                          Add
                        </Button>
                      </Box>

                      {selectedField.options?.map((option, index) => (
                        <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                          <TextField
                            size="small"
                            label="Label"
                            value={option.label}
                            onChange={(e) => {
                              const newOptions = [...selectedField.options];
                              newOptions[index] = { ...option, label: e.target.value };
                              handleFieldUpdate(selectedField.id, { options: newOptions });
                            }}
                            sx={{ mr: 1, flexGrow: 1 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveOption(selectedField.id, index)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a field from the canvas to edit its properties
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Load Form Dialog */}
        <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Load Saved Form</DialogTitle>
          <DialogContent>
            {savedForms.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No saved forms found.
              </Typography>
            ) : (
              <List>
                {savedForms.map((form) => (
                  <ListItem key={form.id} divider>
                    <ListItemText
                      primary={form.title}
                      secondary={`Created: ${new Date(form.createdAt).toLocaleDateString()} • ${form.fields.length} fields`}
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleLoadForm(form)}
                        >
                          Load
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSavedForm(form.id)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoadDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DragDropContext>
  );
};

export default FormBuilder;
